import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CancelTokenSource } from 'axios';
import { QuantityValidationData } from 'components/consultations/UpsertConsultation';
import BillOptionSegment from 'components/costestimation/segments/BillOptionSegment';
import DetailsSegment from 'components/costestimation/segments/DetailsSegment';
import { DropdownOption } from 'components/final-form/dropdownUtils';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import AccompanyingTextSegment from 'components/segments/AccompanyingTextSegment';
import {
  EntriesConsumerRenderProps,
  ExtendedBaseEntryDto,
  withEntriesContext
} from 'components/segments/entires/EntriesContext';
import EntriesSegment from 'components/segments/entires/EntriesSegment';
import MedicamentModal from 'components/segments/medicaments/MedicamentModal';
import ProcedureModal from 'components/segments/procedures/ProcedureModal';
import ProceduresSegment, { IndexedSearchData } from 'components/segments/procedures/ProceduresSegment';
import SpecialProcedureModal from 'components/segments/procedures/SpecialProcedureModal';
import TarmedProcedureModal from 'components/segments/procedures/TarmedProcedureModal';
import { FormState } from 'final-form';
import React, { Component } from 'react';
import { Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Dimmer, Grid, Loader, Modal } from 'semantic-ui-react';
import { editCostEstimation, getUpsertCostEstimation, saveCostEstimation } from 'services/costEstimationServices';
import { getAllGuarantors } from 'services/guarantorServices';
import axios from 'services/http';
import { debounce } from 'ts-debounce';
import { EntryTable } from 'ts-types/api.enums';
import {
  BaseEntryDto,
  CostEstimationDto,
  CostEstimationEntryDto,
  GuarantorDto,
  UpsertCostEstimationDto
} from 'ts-types/api.types';

const leftSegmentStyle = {
  width: "45%",
  paddingLeft: "1rem"
};

const rightSegmentStyle = {
  width: "55%",
  paddingLeft: "2rem",
  paddingRight: "1rem"
};

export enum Views {
  NONE,
  GUARANTORS,
  SPECIAL
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps, EntriesConsumerRenderProps {
  modalOpen: boolean,
  onCloseModal: () => void,
  onConfirmAction: (costEstimation: CostEstimationDto) => void,
  costEstimationId?: number,
  setCacheSearchId: (searchId?: string) => void,
  tariffId: number,
  patientId: number,
  tarmedTariff: boolean,
  readOnly: boolean
}

interface State {
  costEstimation?: UpsertCostEstimationDto,
  guarantors: Array<DropdownOption>,
  loadingData: boolean,
  accompanyingTextModalOpen: boolean,
  editTarmedProcedureModalOpen: boolean,
  specialProcedureModalOpen: boolean,
  editEntry?: ExtendedBaseEntryDto,
  editProcedureModalOpen: boolean,
  editMedicamentModalOpen: boolean,
  accompanyingText: string,
  visibleView: Views,
  errorMessages?: Array<string>,
  searchData: Partial<IndexedSearchData>,
  quantityValidationData: QuantityValidationData,
  cancelTokenSource: CancelTokenSource
}

class UpsertCostEstimation extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const {tariffId, patientId, currentUser, costEstimationId, setCurrentEntries, options: {workDate}} = this.props;

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      guarantors: [],
      loadingData: !!costEstimationId,
      accompanyingTextModalOpen: false,
      editTarmedProcedureModalOpen: false,
      specialProcedureModalOpen: false,
      editProcedureModalOpen: false,
      editMedicamentModalOpen: false,
      accompanyingText: "",
      searchData: {
        validDate: workDate,
        tariffId: tariffId,
        patientId: patientId,
        mandateId: currentUser!.userConfig.mandateId
      },
      quantityValidationData: {
        patientId: patientId,
        validDate: workDate,
        electronicDataTransfer: false,
        tariffId: tariffId
      },
      visibleView: Views.NONE,
      cancelTokenSource: cancelTokenSource
    };

    let methods: Array<any> = [getAllGuarantors(cancelTokenSource)];

    if (costEstimationId) {
      methods = [...methods, getUpsertCostEstimation(costEstimationId, cancelTokenSource)]
    }

    Promise.all(methods)
        .then(responses => {

          const costEstimation: UpsertCostEstimationDto = responses[1] ? responses[1] : undefined;

          if (costEstimation) {
            setCurrentEntries(costEstimation.costEstimationEntries);
          }

          const mappedGuarantors: Array<DropdownOption> = responses[0].map((guarantor: GuarantorDto) => ({
            key: guarantor.id,
            text: `${guarantor.description}, ${guarantor.city}, ${guarantor.street ? guarantor.street : ""}`,
            value: guarantor.id
          }));

          this.setState({
            guarantors: mappedGuarantors,
            costEstimation: costEstimation,
            loadingData: false
          })
        })
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  initialCostEstimationValues = (): Partial<UpsertCostEstimationDto> => {

    const {options: {workDate}} = this.props;

    return {
      estimationDate: workDate
    }
  };

  toggleAccompanyingTextModal = () => {
    this.setState(({accompanyingTextModalOpen}: State) => ({
      accompanyingTextModalOpen: !accompanyingTextModalOpen
    }));
  };

  onSaveAccompanyingText = (text: string) => {
    this.setState({
      accompanyingText: text
    });

    this.toggleAccompanyingTextModal();
  };

  toggleEditTarmedProcedureModal = () => {
    this.setState(({editTarmedProcedureModalOpen}: State) => ({
      editTarmedProcedureModalOpen: !editTarmedProcedureModalOpen
    }))
  };

  toggleSpecialProcedureModalOpen = () => {
    this.setState(({specialProcedureModalOpen}: State) => ({
      specialProcedureModalOpen: !specialProcedureModalOpen
    }))
  };

  toggleEditProcedureModal = () => {
    this.setState(({editProcedureModalOpen}: State) => ({
      editProcedureModalOpen: !editProcedureModalOpen
    }))
  };

  toggleEditMedicamentModal = () => {
    this.setState(({editMedicamentModalOpen}: State) => ({
      editMedicamentModalOpen: !editMedicamentModalOpen
    }))
  };

  onAddressRadioChange = (selectedView: Views) => {
    this.setState({
      visibleView: selectedView
    })
  };

  onUpsertTarmedProcedure = (tarmedProcedure: ExtendedBaseEntryDto) => {

    this.setState({
      editEntry: tarmedProcedure
    });

    this.toggleEditTarmedProcedureModal();
  };

  onEditProcedure = (procedure: ExtendedBaseEntryDto) => {

    this.setState({
      editEntry: procedure
    });

    this.toggleEditProcedureModal();
  };

  onEditMedicament = (medicament: ExtendedBaseEntryDto) => {

    this.setState({
      editEntry: medicament
    });

    this.toggleEditMedicamentModal();
  };

  onUpsertSpecialProcedure = (specialProcedure?: ExtendedBaseEntryDto) => {

    this.setState({
      editEntry: specialProcedure
    });

    this.toggleSpecialProcedureModalOpen();
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessages: errorMessage ? [errorMessage] : undefined
    })
  };

  setErrorMessages = (errorMessages?: Array<string>) => {
    this.setState({
      errorMessages: errorMessages
    })
  };

  onCloseModal = () => {

    const {onCloseModal, setCurrentEntries} = this.props;

    setCurrentEntries([]);

    onCloseModal();
  };

  handleChange = debounce(({values}: FormState) => {

    if (!this.mounted) {
      return;
    }

    const {searchData, quantityValidationData} = this.state;

    const validDate = searchData.validDate;

    if (values.estimationDate != validDate) {
      this.setState({
        searchData: {
          ...searchData,
          validDate: values.estimationDate
        },
        quantityValidationData: {
          ...quantityValidationData,
          validDate: values.estimationDate
        }
      })
    }
  }, 300);

  handleSubmit = async (values: Partial<UpsertCostEstimationDto>) => {

    const {currentEntries, tariffId, costEstimationId, getMessage, onConfirmAction, setCurrentEntries, patientId} = this.props;
    const {accompanyingText, cancelTokenSource} = this.state;

    values.text = accompanyingText == "" ? undefined : accompanyingText;

    let costEstimationEntries: Array<CostEstimationEntryDto> = [];

    const mapEntry = (entry: BaseEntryDto): CostEstimationEntryDto => {
      return {
        ...entry,
        costEstimationEntryId: entry.parentEntryId
      }
    };

    currentEntries.forEach(({quantityFromDb, ...currentEntry}: ExtendedBaseEntryDto) => {

      if (currentEntry.bulks) {
        const bulkEntries: Array<CostEstimationEntryDto> | undefined = currentEntry.bulks
            .filter(bulk => bulk.quantity > 0 && bulk.code != currentEntry.code)
            .map(bulk => mapEntry(bulk));

        if (bulkEntries != undefined) {
          costEstimationEntries.push(...bulkEntries);
        }
      }

      if (currentEntry.descendants) {
        const descendantEntries: Array<CostEstimationEntryDto> | undefined = currentEntry.descendants
            .filter(descendant => descendant.quantity > 0)
            .map(descendant => mapEntry(descendant));

        if (descendantEntries != undefined) {
          costEstimationEntries.push(...descendantEntries);
        }
      }

      costEstimationEntries.push(mapEntry(currentEntry))
    });

    values.costEstimationEntries = costEstimationEntries;
    values.tariffId = tariffId;
    values.patientId = patientId;

    const handleError = () => this.setErrorMessage(getMessage("error.general"));

    if (costEstimationId) {
      await editCostEstimation(costEstimationId, values, cancelTokenSource)
          .then(response => {
            onConfirmAction(response);
            setCurrentEntries([]);
            this.onCloseModal();
          })
          .catch(handleError)
    } else {
      await saveCostEstimation(values, cancelTokenSource)
          .then(response => {
            onConfirmAction(response);
            setCurrentEntries([]);
            this.onCloseModal();
          })
          .catch(handleError)
    }
  };

  render(): React.ReactNode {

    const {loadingData} = this.state;

    return loadingData ? this.renderLoader() : this.renderModal();
  }

  renderModal(): React.ReactNode {

    const {modalOpen, onCloseModal, getMessage} = this.props;
    const {costEstimation} = this.state;

    return (
        <Modal
            open={modalOpen}
            onClose={onCloseModal}
            style={{width: "1500px"}}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("costEstimation")}</Modal.Header>
          <FormModalContentContainer>
            <FinalForm
                onSubmit={this.handleSubmit}
                subscription={{pristine: true, values: true, submitting: true}}
                render={this.renderForm}
                initialValues={costEstimation ? costEstimation : this.initialCostEstimationValues()}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };

  renderForm = ({handleSubmit, values, submitting}: FormRenderProps): React.ReactNode => {

    const {costEstimationId, tarmedTariff, setCacheSearchId, getMessage, readOnly} = this.props;
    const {
      guarantors,
      errorMessages,
      searchData,
      accompanyingTextModalOpen,
      editTarmedProcedureModalOpen,
      editEntry,
      quantityValidationData,
      editProcedureModalOpen,
      editMedicamentModalOpen,
      specialProcedureModalOpen,
      visibleView
    } = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessages
              && errorMessages.length > 0
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessages()}>
                    {
                      errorMessages.map((errorMessage, index) => {
                        return (
                            <div key={index}>{errorMessage}</div>
                        )
                      })
                    }
                  </StyledErrorMessage>
              )
              }
              <PraxFormGrid>
                <Grid.Column style={leftSegmentStyle}>
                  <ProceduresSegment
                      searchData={searchData}
                      parentId={costEstimationId}
                      onUpsertTarmedProcedure={this.onUpsertTarmedProcedure}
                      onEditProcedure={this.onEditProcedure}
                      onEditMedicament={this.onEditMedicament}
                      onErrors={this.setErrorMessages}
                      onError={this.setErrorMessage}
                      setCacheSearchId={setCacheSearchId}
                      entryTable={EntryTable.COST_ESTIMATION}
                  />
                </Grid.Column>
                <Grid.Column style={rightSegmentStyle}>
                  <DetailsSegment/>
                  <BillOptionSegment
                      visibleView={visibleView}
                      onAddressRadioChange={this.onAddressRadioChange}
                      guarantors={guarantors}
                  />
                  <EntriesSegment
                      tarmedTariff={tarmedTariff}
                      headerText={getMessage("accumulatedValue.short")}
                      onUpsertTarmedProcedure={this.onUpsertTarmedProcedure}
                      onUpsertSpecialProcedure={this.onUpsertSpecialProcedure}
                      onEditProcedure={this.onEditProcedure}
                      onEditMedicament={this.onEditMedicament}
                      componentHeight="305px"
                      visibleView={visibleView}
                  />
                </Grid.Column>
                <FormSpy subscription={{values: true}} onChange={this.handleChange}/>

                <AccompanyingTextSegment
                    modalOpen={accompanyingTextModalOpen}
                    onCloseModal={this.toggleAccompanyingTextModal}
                    onSaveAction={this.onSaveAccompanyingText}
                    accompanyingText={values.text ? values.text : ""}
                />
                <TarmedProcedureModal
                    modalOpen={editTarmedProcedureModalOpen}
                    onCloseModal={this.toggleEditTarmedProcedureModal}
                    tarmedProcedure={editEntry}
                    tariffId={searchData.tariffId}
                    quantityValidationData={quantityValidationData}
                    entryTable={EntryTable.COST_ESTIMATION}
                    validDate={searchData.validDate}
                />
                <ProcedureModal
                    modalOpen={editProcedureModalOpen}
                    onCloseModal={this.toggleEditProcedureModal}
                    procedure={editEntry}
                />
                {
                  editMedicamentModalOpen &&
                  <MedicamentModal
                      modalOpen={editMedicamentModalOpen}
                      onCloseModal={this.toggleEditMedicamentModal}
                      medicament={editEntry}
                  />
                }
                <SpecialProcedureModal
                    modalOpen={specialProcedureModalOpen}
                    onCloseModal={this.toggleSpecialProcedureModalOpen}
                    specialProcedure={editEntry}
                />
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button type="submit" compact primary
                      disabled={submitting || readOnly}>{getMessage("button.save")}</Button>
              <Button type="button" compact disabled={submitting}
                      onClick={this.onCloseModal}>{getMessage("button.cancel")}</Button>
              <Button type="button" compact floated={"right"} primary disabled={submitting}
                      onClick={this.toggleAccompanyingTextModal}>{getMessage("accompanyingText.short")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    )
  };

  renderLoader = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <Dimmer active page>
          <Loader inline='centered' size="medium" inverted>{getMessage("consultationForm.loading")}</Loader>
        </Dimmer>
    )
  };
}

export default withMessages(withAuthContext(withEntriesContext(UpsertCostEstimation)));
